import { createTheme, CSSVariablesResolver, defaultVariantColorsResolver, type MantineTheme, type VariantColorsResolver } from '@mantine/core';
import { BRAND, DANGER, DARK, INFO, SPACE, SUCCESS, TAG_PALETTE, TAG_TINT, TYPE_SCALE, WARNING } from './tokens';

const INPUT_WRAPPER_ORDER: ('label' | 'input' | 'description' | 'error')[] = [
    'label', 'input', 'description', 'error',
];

const inputDefaults = {
    defaultProps: { size: 'sm', radius: 'md', inputWrapperOrder: INPUT_WRAPPER_ORDER },
};

const SAFE_TOP = 'env(safe-area-inset-top, 0px)';
const SAFE_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

const PRESS_TRANSITION = 'background-color 120ms ease-out, border-color 120ms ease-out, color 120ms ease-out, box-shadow 120ms ease-out, transform 80ms ease-out';

const variantColorResolver: VariantColorsResolver = (input) => {
    const resolved = defaultVariantColorsResolver(input);
    const brand = !input.color || input.color === 'brand' || input.color === input.theme.primaryColor;
    if (input.variant === 'filled' && brand) return { ...resolved, color: 'var(--bd-on-brand)' };
    return resolved;
};

export const SURFACE = { light: '#FFFFFF', dark: '#15161B' } as const;

export const theme = createTheme({
    variantColorResolver,
    primaryColor: 'brand',
    primaryShade: { light: 7, dark: 4 },
    defaultRadius: 'md',
    colors: {
        brand: BRAND,
        success: SUCCESS,
        warning: WARNING,
        danger: DANGER,
        info: INFO,
        dark: DARK,
    },
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontFamilyMonospace: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    fontSizes: {
        xs: TYPE_SCALE.xs,
        sm: TYPE_SCALE.sm,
        md: TYPE_SCALE.md,
        lg: TYPE_SCALE.lg,
        xl: TYPE_SCALE.xl,
    },
    lineHeights: { xs: '1.4', sm: '1.45', md: '1.55', lg: '1.55', xl: '1.4' },
    spacing: SPACE,
    headings: {
        fontWeight: '700',
        sizes: {
            h1: { fontSize: TYPE_SCALE.h1, lineHeight: '1.2', fontWeight: '700' },
            h2: { fontSize: TYPE_SCALE.xl, lineHeight: '1.25', fontWeight: '700' },
            h3: { fontSize: TYPE_SCALE.lg, lineHeight: '1.3', fontWeight: '600' },
            h4: { fontSize: TYPE_SCALE.md, lineHeight: '1.4', fontWeight: '600' },
        },
    },
    cursorType: 'pointer',
    focusRing: 'auto',

    shadows: {
        xs: '0 1px 2px rgba(8, 10, 18, 0.08)',
        sm: '0 1px 3px rgba(8, 10, 18, 0.10), 0 6px 16px -8px rgba(8, 10, 18, 0.14)',
        md: '0 4px 16px -6px rgba(8, 10, 18, 0.20), 0 2px 6px -2px rgba(8, 10, 18, 0.12)',
        lg: '0 12px 32px -12px rgba(8, 10, 18, 0.32), 0 4px 10px -4px rgba(8, 10, 18, 0.16)',
        xl: '0 24px 56px -20px rgba(8, 10, 18, 0.42)',
    },

    components: {
        Paper: {
            defaultProps: { shadow: 'none' },
            styles: { root: { backgroundColor: 'var(--bd-surface)' } },
        },
        Card: {
            defaultProps: { withBorder: true, padding: 'lg', radius: 'lg' },
            styles: { root: { backgroundColor: 'var(--bd-surface)' } },
        },
        Menu: { styles: { dropdown: { backgroundColor: 'var(--bd-surface)' } } },
        Popover: { styles: { dropdown: { backgroundColor: 'var(--bd-surface)' } } },
        HoverCard: { styles: { dropdown: { backgroundColor: 'var(--bd-surface)' } } },
        Combobox: { styles: { dropdown: { backgroundColor: 'var(--bd-surface)' } } },

        InputWrapper: { defaultProps: { inputWrapperOrder: INPUT_WRAPPER_ORDER } },
        TextInput: inputDefaults,
        Textarea: inputDefaults,
        NumberInput: inputDefaults,
        PasswordInput: inputDefaults,
        Select: inputDefaults,
        MultiSelect: inputDefaults,
        Autocomplete: inputDefaults,
        Checkbox: { defaultProps: { radius: 'sm' } },
        SegmentedControl: { defaultProps: { radius: 'md' } },

        Button: { defaultProps: { radius: 'md' }, styles: { root: { transition: PRESS_TRANSITION } } },
        ActionIcon: { defaultProps: { radius: 'md' }, styles: { root: { transition: PRESS_TRANSITION } } },
        Badge: { defaultProps: { radius: 'sm' }, styles: { root: { textTransform: 'none', fontWeight: 600, flexShrink: 0, maxWidth: 'none', overflow: 'visible' }, label: { overflow: 'visible' } } },
        Tooltip: {
            defaultProps: { withArrow: true, openDelay: 250, transitionProps: { duration: 140 } },
        },
        Modal: {
            defaultProps: { overlayProps: { backgroundOpacity: 0.55, blur: 4 }, radius: 'lg' },
            styles: (_theme: MantineTheme, props: { fullScreen?: boolean }) => ({
                content: { backgroundColor: 'var(--bd-surface)' },
                header: {
                    backgroundColor: 'var(--bd-surface)',
                    ...(props.fullScreen ? { paddingTop: `calc(var(--mb-padding, var(--mantine-spacing-md)) + ${SAFE_TOP})` } : {}),
                },
                body: props.fullScreen ? { paddingBottom: `calc(var(--mb-padding, var(--mantine-spacing-md)) + ${SAFE_BOTTOM})` } : {},
            }),
        },
        Drawer: {
            defaultProps: { overlayProps: { backgroundOpacity: 0.55, blur: 4 } },
            styles: (_theme: MantineTheme, props: { position?: string }) => ({
                content: { backgroundColor: 'var(--bd-surface)' },
                header: props.position === 'bottom' ? {} : { paddingTop: `calc(var(--mb-padding, var(--mantine-spacing-md)) + ${SAFE_TOP})` },
                body: props.position === 'top' ? {} : { paddingBottom: `calc(var(--mb-padding, var(--mantine-spacing-md)) + ${SAFE_BOTTOM})` },
            }),
        },
        Table: { styles: { table: { fontVariantNumeric: 'tabular-nums' } } },
    },
});

const tagVariables = (mode: 'light' | 'dark'): Record<string, string> => Object.fromEntries(
    Object.keys(TAG_PALETTE).flatMap((name) => {
        const key = name as keyof typeof TAG_PALETTE;
        return [
            [`--bd-tag-${name}`, TAG_PALETTE[key][mode]],
            [`--bd-tag-${name}-soft`, TAG_TINT[key][mode]],
        ];
    }),
);

export const cssVariablesResolver: CSSVariablesResolver = (t) => ({
    variables: {
        '--bd-radius-tile': t.radius.md,

        '--bd-map-ground': '#0A0B0D',
        '--bd-map-inert': '#16181D',
        '--bd-map-border': '#24272E',
        '--bd-map-1': t.colors.brand[8],
        '--bd-map-2': t.colors.brand[6],
        '--bd-map-3': t.colors.brand[5],
        '--bd-map-4': t.colors.brand[4],
        '--bd-map-dot': t.colors.brand[0],
    },
    light: {
        '--mantine-color-body': '#F5F6F8',
        '--mantine-color-dimmed': t.colors.gray[7],
        '--bd-surface': SURFACE.light,
        '--bd-surface-hover': t.colors.gray[0],
        '--bd-surface-sunken': '#EEF0F3',
        '--bd-shell': '#FFFFFF',
        '--bd-border': t.colors.gray[3],
        '--bd-border-strong': t.colors.gray[4],
        '--bd-text-strong': t.colors.gray[9],
        '--bd-brand-soft': t.colors.brand[0],
        '--bd-selection': t.colors.brand[1],
        '--bd-brand-text': t.colors.brand[7],
        '--bd-on-brand': '#FFFFFF',
        '--bd-brand-glow': 'rgba(13, 148, 136, 0.18)',
        '--bd-status-success': t.colors.success[6],
        '--bd-status-success-soft': t.colors.success[0],
        '--bd-status-warning': t.colors.warning[6],
        '--bd-status-warning-soft': t.colors.warning[0],
        '--bd-status-danger': t.colors.danger[6],
        '--bd-status-danger-soft': t.colors.danger[0],
        '--bd-status-info': t.colors.info[6],
        '--bd-status-info-soft': t.colors.info[0],
        '--bd-chart-grid': t.colors.gray[2],
        ...tagVariables('light'),
    },
    dark: {
        '--mantine-color-body': '#0C0D10',
        '--mantine-color-dimmed': t.colors.dark[2],
        '--bd-surface': SURFACE.dark,
        '--bd-surface-hover': '#1B1D23',
        '--bd-surface-sunken': '#08090B',
        '--bd-shell': '#0F1013',
        '--bd-border': t.colors.dark[6],
        '--bd-border-strong': t.colors.dark[5],
        '--bd-text-strong': '#ECEDEF',
        '--bd-brand-soft': 'rgba(45, 212, 191, 0.10)',
        '--bd-selection': 'rgba(45, 212, 191, 0.28)',
        '--bd-brand-text': t.colors.brand[3],
        '--bd-on-brand': '#042F2E',
        '--bd-brand-glow': 'rgba(45, 212, 191, 0.20)',
        '--bd-status-success': t.colors.success[4],
        '--bd-status-success-soft': 'rgba(74, 222, 128, 0.12)',
        '--bd-status-warning': t.colors.warning[4],
        '--bd-status-warning-soft': 'rgba(251, 191, 36, 0.12)',
        '--bd-status-danger': t.colors.danger[4],
        '--bd-status-danger-soft': 'rgba(248, 113, 113, 0.14)',
        '--bd-status-info': t.colors.info[4],
        '--bd-status-info-soft': 'rgba(96, 165, 250, 0.12)',
        '--bd-chart-grid': t.colors.dark[6],
        ...tagVariables('dark'),
    },
});
